import { ChevronRight } from 'lucide-react';

import type {
  CandidateListResult,
  CandidateRowResult,
} from '@/modules/hiring-manager/chat/chatResults';

import { SendButton } from './SendButton';

interface CandidateListProps {
  list: CandidateListResult;
  /** Opens the row's candidate beside the chat. */
  onOpen: (row: CandidateRowResult) => void;
  /** Sends a button's message as the HM's next message. */
  onSend: (text: string) => void;
  /** A turn or a click is running: the buttons wait. */
  disabled: boolean;
  /** The message being sent, so the button that sent it shows it's working. */
  sending?: string;
}

/**
 * Candidates Finlay listed, under its reply: a row each, which opens the candidate beside the chat, with the decisions
 * waiting on the HM. A decision asks Finlay, which shows the change to confirm.
 */
export function CandidateList({
  list,
  onOpen,
  onSend,
  disabled,
  sending,
}: CandidateListProps) {
  const shown =
    list.total > list.rows.length
      ? `${list.rows.length} of ${list.total}`
      : null;

  return (
    <section
      aria-label={list.title}
      className="flex flex-col rounded-2xl border text-[13px]"
    >
      <header className="flex items-baseline justify-between gap-3 border-b px-4 py-2.5">
        <h3 className="font-semibold">{list.title}</h3>
        {shown && (
          <span className="text-muted-foreground text-xs">{shown}</span>
        )}
      </header>
      <ul className="divide-y">
        {list.rows.map((row) => {
          const about = row.detail ?? row.headline;
          return (
            <li
              className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5"
              key={row.ref}
            >
              <button
                className="group flex min-w-0 flex-1 items-center gap-2 text-left"
                onClick={() => onOpen(row)}
                type="button"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate">
                    <span className="font-medium group-hover:underline">
                      {row.name}
                    </span>
                    <span className="text-muted-foreground">
                      {' '}
                      · {row.job.title}
                      {row.stageLabel ? ` · ${row.stageLabel}` : ''}
                    </span>
                  </span>
                  {about && (
                    <span className="text-muted-foreground block truncate">
                      {about}
                    </span>
                  )}
                  {row.fit && (
                    <span className="block truncate text-neutral-500">
                      {row.fit}
                    </span>
                  )}
                </span>
                <ChevronRight className="text-muted-foreground size-3.5 shrink-0" />
              </button>
              {row.actions.length > 0 && (
                <div className="flex gap-2">
                  {row.actions.map((action) => (
                    <SendButton
                      action={action}
                      disabled={disabled}
                      key={action.label}
                      onSend={onSend}
                      sending={sending}
                    />
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {list.more && (
        <div className="border-t px-4 py-2">
          <SendButton
            action={list.more}
            disabled={disabled}
            onSend={onSend}
            sending={sending}
            variant="ghost"
          />
        </div>
      )}
    </section>
  );
}
